import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LanguageIcon from '@mui/icons-material/Language';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useThemeContext } from '../context/ThemeContext';
import { useLanguageContext } from '../context/LanguageContext';
import { translations } from '../translations';

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { darkMode, toggleDarkMode } = useThemeContext();
  const { language, toggleLanguage } = useLanguageContext();
  const t = translations[language];

  const navItems = [
    { text: t.nav.home, path: '/' },
    { text: t.nav.about, path: '/about' },
    { text: t.nav.projects, path: '/projects' },
    { text: t.nav.contact, path: '/contact' }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation" onClick={handleDrawerToggle}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          
        </Typography>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem 
              button 
              component={RouterLink} 
              to={item.path} 
              key={item.text}
              sx={{
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  width: active ? '80%' : '0%',
                  height: '2px',
                  backgroundColor: 'primary.main',
                  transform: 'translateX(-50%)',
                  transition: 'width 0.3s ease-in-out',
                },
                '&:hover::after': {
                  width: '80%',
                }
              }}
            >
              <ListItemText 
                primary={item.text} 
                sx={{ 
                  textAlign: 'center',
                  color: active ? 'primary.main' : 'inherit',
                  fontWeight: active ? 600 : 400,
                }} 
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <AppBar position="sticky" elevation={1}>
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 700,
              '&:hover': {
                color: 'primary.main'
              },
              transition: 'color 0.3s ease-in-out'
            }}
          >
            Ulrich NDELOKO
          </Typography>

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {navItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Button
                      key={item.text}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: active && darkMode ? 'primary.main' : 'white',
                        position: 'relative',
                        textTransform: 'none',
                        fontWeight: active ? 600 : 400,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 4,
                          left: '50%',
                          width: active ? '60%' : '0%',
                          height: '2px',
                          backgroundColor: darkMode ? 'primary.main' : 'white',
                          transform: 'translateX(-50%)',
                          transition: 'width 0.3s ease-in-out',
                        },
                        '&:hover::after': {
                          width: '60%',
                        },
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: darkMode ? 'primary.main' : 'white',
                        }
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={t.nav.toggleLanguage}>
                  <IconButton
                    onClick={toggleLanguage}
                    color="inherit"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'white',
                      },
                    }}
                  >
                    <LanguageIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={darkMode ? t.nav.lightMode : t.nav.darkMode}>
                  <IconButton
                    onClick={toggleDarkMode}
                    color="inherit"
                    sx={{
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'white',
                      },
                    }}
                  >
                    {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={t.nav.toggleLanguage}>
                <IconButton
                  onClick={toggleLanguage}
                  color="inherit"
                  size="large"
                >
                  <LanguageIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={darkMode ? t.nav.lightMode : t.nav.darkMode}>
                <IconButton
                  onClick={toggleDarkMode}
                  color="inherit"
                  size="large"
                >
                  {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </Tooltip>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
}

export default Navbar;
